import type { RecognitionLevelParams } from '../progression/levels.js';
import { pickRandom, shuffle } from './random.js';

/**
 * Visual confusion pairs for digit recognition. When picking distractors,
 * prefer one confusable digit so the trial probes shape-discrimination, not
 * blind guessing.
 */
const CONFUSION_PAIRS: Readonly<Record<number, readonly number[]>> = {
  0: [6, 8, 9],
  1: [7],
  2: [5],
  3: [8],
  4: [7],
  5: [2],
  6: [9, 0],
  7: [1, 4],
  8: [3, 6, 0],
  9: [6, 0, 8],
};

export function pickRecognitionDistractors(
  target: number,
  pool: readonly number[],
  distractorCount: number,
  rng: () => number,
): number[] {
  if (distractorCount <= 0) return [];
  const candidates = pool.filter((d) => d !== target);
  const distractors: number[] = [];

  // Prefer one confusable digit if any are available within the level pool.
  const confusables = (CONFUSION_PAIRS[target] ?? []).filter((d) => candidates.includes(d));
  if (confusables.length > 0) {
    distractors.push(pickRandom(confusables, rng));
  }

  // Fill remaining slots from the pool, excluding already-picked.
  const remaining = candidates.filter((d) => !distractors.includes(d));
  while (distractors.length < distractorCount && remaining.length > 0) {
    const idx = Math.floor(rng() * remaining.length);
    const [picked] = remaining.splice(idx, 1);
    distractors.push(picked!);
  }

  return distractors;
}

export interface RecognitionTrial {
  target: number;
  /** Tiles in display order (target + distractors, shuffled). */
  tiles: number[];
}

export function generateRecognitionTrial(
  params: RecognitionLevelParams,
  rng: () => number = Math.random,
): RecognitionTrial {
  const target = pickRandom(params.digits, rng);
  const distractors = pickRecognitionDistractors(target, params.digits, params.tileCount - 1, rng);
  const tiles = shuffle([target, ...distractors], rng);
  return { target, tiles };
}
